import React, { CSSProperties, FC } from 'react'
import { PlugMark, PlugMarkProps } from '../PlugMark'
import { PFIELD_CANVAS, PFIELD_MARK, PFIELD_RINGS } from './pfieldRings'

/** breathe = idle, pulse = live/loading, ripple = done (plays once), listen = waiting on the user or on AI. */
export type PFieldState = 'none' | 'breathe' | 'pulse' | 'ripple' | 'listen'

const MOTION: Record<
  Exclude<PFieldState, 'none'>,
  { period: number; stagger: number; direction: 'in' | 'out' }
> = {
  breathe: { period: 6000, stagger: 90, direction: 'out' },
  pulse: { period: 2400, stagger: 110, direction: 'out' },
  ripple: { period: 1200, stagger: 70, direction: 'out' },
  listen: { period: 1600, stagger: 90, direction: 'in' },
}

export interface PFieldProps {
  state?: PFieldState
  /** Extra delay on every ring in ms. Negative starts mid-cycle; -3000 starts breathe at its peak. */
  phase?: number
  /** Multiplier on ring opacity. Defaults to the tone's --plug-field-intensity (1 night, 0.82 flux). */
  intensity?: number
  /** Hairline in CSS px; does not scale with the canvas. */
  strokeWidth?: number
  mark?: Omit<PlugMarkProps, 'x' | 'y' | 'width' | 'height'>
  className?: string
  style?: CSSProperties
}

/** The eleven P rings from the kit, animated by plug-motion.css. */
export const PField: FC<PFieldProps> = ({
  state = 'breathe',
  phase = 0,
  intensity,
  strokeWidth = 1.4,
  mark,
  className,
  style,
}) => {
  const motion = state === 'none' ? undefined : MOTION[state]
  const last = PFIELD_RINGS.length - 1
  const delayFor = (k: number) => {
    if (!motion) return undefined
    const step = motion.direction === 'out' ? k : last - k
    return `${step * motion.stagger + phase}ms`
  }
  return (
    <svg
      viewBox={`0 0 ${PFIELD_CANVAS} ${PFIELD_CANVAS}`}
      overflow="visible"
      aria-hidden="true"
      focusable="false"
      className={className}
      style={style}
    >
      {/* plug-motion.css keys its keyframes off .plug-field--<state> path; delays are inline so phase can be controlled. */}
      <g
        className={state === 'none' ? 'plug-field' : `plug-field plug-field--${state}`}
        style={{ opacity: intensity ?? 'var(--plug-field-intensity, 1)' }}
      >
        {PFIELD_RINGS.map((ring, k) => (
          <path
            key={k}
            d={ring.d}
            fill="none"
            stroke={ring.stroke}
            strokeOpacity={ring.opacity}
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
            style={{ animationDelay: delayFor(k) }}
          />
        ))}
      </g>
      {mark && (
        <PlugMark
          {...mark}
          x={PFIELD_MARK.x}
          y={PFIELD_MARK.y}
          width={PFIELD_MARK.width}
          height={PFIELD_MARK.height}
        />
      )}
    </svg>
  )
}
